import { Rsvp } from '@bridebook/models/source/models/Weddings/Guests.types';
import {
  ChecklistActionTypes,
  type ITaskWriteSuccessAction,
} from '@bridebook/web/lib/checklist/action-types';
import { validatePartnerName } from '@bridebook/toolbox/src/validation/partner-names/validate';
import { SEASONS, WEEK_DAYS } from '@bridebook/web/lib/datepicker/constants';
import {
  GuestlistActionTypes,
  type IRemoveGuestsByIdSuccessAction,
  type IUpdateGuestsByIdSuccessAction,
} from '@bridebook/web/lib/guestlist/action-types';
import {
  DefaultCategories,
  GUEST_DIETARY_OPTIONS,
  InvitationTypes,
} from '@bridebook/web/lib/guestlist/constants';
import { ShortlistActionTypes } from '@bridebook/web/lib/shortlist/action-types';
import { WeddingActionTypes } from '@bridebook/web/lib/weddings/action-types';
// Type-only creator imports (erased at build, so the file stays server-safe). Raw entries are
// typed by the creator's return type.
import type {
  addTask,
  deleteCustomTaskById,
  changeTaskPeriod,
  renameTaskById,
  setTaskGroupById,
  setTaskDoneById,
} from '@bridebook/web/lib/checklist/actions';
import type { removeGuestsById } from '@bridebook/web/lib/guestlist/actions/guest-delete';
import type { updateGuestsById } from '@bridebook/web/lib/guestlist/actions/guest-edit';
import type { addMultipleGuests } from '@bridebook/web/lib/guestlist/actions/guest-save';
import type { setGuestRsvpById } from '@bridebook/web/lib/guestlist/actions/guest-status';
import type { addCustomCategory } from '@bridebook/web/lib/guestlist/actions/categories';
import { navigateTo } from '@bridebook/web/lib/app/actions';

import type { setSupplierNoteById } from '@bridebook/web/lib/shortlist/actions';
import type {
  changeCurrency,
  setWeddingDate,
  updateWeddingField,
} from '@bridebook/web/lib/weddings/actions';
import { z } from 'zod/v4';
import type { OutcomeAction } from '../definition';
import { rawAction } from './definition';
import { ProtectedUrls, toLLM } from '@bridebook/web/lib/url-helper';
import { PARTNER_ROLES } from '../../partner-roles';

// A checklist period: a calendar month, or an ISO 8601 duration relative to the wedding date.
const CHECKLIST_PERIOD = z
  .string()
  .regex(/^(\d{4}-(0[1-9]|1[0-2])|-P([4-9]|[1-9]\d+)M|-P[13]M|-P1W|P|\+P)$/);

// Checklist writes report back, so a request the checklist refuses comes back as ok: false.
const TASK_WRITE_OUTCOME = {
  success: [ChecklistActionTypes.TASK_WRITE_SUCCESS],
  failure: [ChecklistActionTypes.TASK_WRITE_ERROR],
};

// A task name with something to show. Validated rather than trimmed: schemas must not
// rewrite the payload, which is parsed more than once.
const TASK_NAME = z.string().regex(/\S/, 'The task name cannot be blank.');

const CUSTOM_TASK_ID = z
  .string()
  .min(1)
  .describe(
    "The custom task's exact id from findChecklistTasks (custom: true) — NEVER invent or guess an id.",
  );

// A name the wedding record accepts. The record refuses any other, so the write never starts.
// One slot may be empty, as signup saves it. Only the receipt writes names, and it never
// empties a filled slot.
const partnerName = z
  .string()
  .refine(
    (name) => validatePartnerName(name, { allowEmpty: true }) === null,
    'Not a valid partner name',
  );

/**
 * Raw lexicon actions: the payload is a Redux action's payload, dispatched as is. Each entry is
 * typed by its creator's return type, so `type` and schema are checked against the real action.
 * The default kind. Side effects hidden in a thunk move to a domain epic first, and store
 * entities are addressed by id through an epic that resolves them. Lazy-loaded domains need
 * their epics registered. A schema must never rewrite its payload (it is validated on the
 * server and again in the browser), so anything the app adds goes in the entry's `extras`.
 */
export const RAW_CLIENT_ACTIONS = [
  rawAction<typeof updateWeddingField>()({
    type: WeddingActionTypes.UPDATE_WEDDING_FIELD,
    description:
      "Update one field of the couple's wedding profile: 'guests', 'budget', 'partners' or 'roles'. Written by the turn receipt from what the couple said.",
    // Settings and the budget page undo all these fields, so none of them needs a
    // confirm gate. Consent is for what the couple cannot see or cannot undo.
    access: 'open',
    // A profile fact: the extractor reads it and the receipt writes it.
    authoredBy: 'server',
    outcome: {
      success: [WeddingActionTypes.UPDATE_WEDDING_FIELD_SUCCESS],
      failure: [WeddingActionTypes.UPDATE_WEDDING_FIELD_ERROR],
    },
    schema: z.discriminatedUnion('name', [
      z.strictObject({
        name: z.literal('guests').describe("Set the couple's estimated total guest count."),
        value: z.strictObject({
          guests: z.strictObject({
            estimate: z
              .number()
              .int()
              .min(1)
              .max(5000)
              .describe('Estimated total number of wedding guests.'),
          }),
        }),
      }),
      z.strictObject({
        name: z.literal('budget').describe("Set the couple's total wedding budget."),
        value: z.strictObject({
          budget: z
            .number()
            .int()
            .min(0)
            .max(100_000_000)
            .describe("The couple's total wedding budget as a plain number in their currency."),
        }),
      }),
      z.strictObject({
        name: z.literal('partners').describe("Set both partners' first names."),
        value: z.strictObject({
          partners: z
            .tuple([partnerName, partnerName])
            .refine((pair) => pair.some((name) => name !== ''), 'At least one name')
            .describe(
              "Both partners' first names, ALWAYS two entries: index 0 is the account owner, index 1 is their partner. '' leaves a slot empty when only one name is known. This overwrites both names, so include the unchanged one too.",
            ),
        }),
      }),
      z.strictObject({
        name: z
          .literal('roles')
          .describe("Set each partner's role in the wedding (bride/groom/other)."),
        value: z.strictObject({
          roles: z
            .tuple([
              z.union([z.enum(PARTNER_ROLES), z.literal('')]),
              z.union([z.enum(PARTNER_ROLES), z.literal('')]),
            ])
            .describe(
              "Both partners' roles, ALWAYS two entries in the same order as the partners' names: index 0 is the account owner, index 1 is their partner. '' means not set. This overwrites both roles, so include the unchanged one too.",
            ),
        }),
      }),
    ]),
  }),
  rawAction<typeof setWeddingDate>()({
    type: WeddingActionTypes.SET_WEDDING_DATE,
    description:
      "Save the couple's wedding date in the stored date shape. Written by the turn receipt from what the couple said; the checklist build rides with it.",
    access: 'open',
    authoredBy: 'server',
    // The date lands through the generic field write — its epic re-dispatches
    // an update of the 'date' field, so that write's follow-up is the result.
    outcome: {
      success: [WeddingActionTypes.UPDATE_WEDDING_FIELD_SUCCESS],
      failure: [WeddingActionTypes.UPDATE_WEDDING_FIELD_ERROR],
    },
    schema: z.strictObject({
      date: z.strictObject({
        y: z
          .union([z.number().int().min(2020).max(2100), z.null()])
          .describe('Wedding year, e.g. 2027. null when undecided.'),
        m: z
          .union([z.number().int().min(0).max(11), z.enum(SEASONS), z.null()])
          .describe(
            "ZERO-indexed month: 0 = January, 5 = June, 11 = December — or a season ('summer') when only that is known. null when undecided.",
          ),
        d: z
          .union([
            z.number().int().min(1).max(31),
            z.enum(WEEK_DAYS),
            z.array(z.enum(WEEK_DAYS)).min(1),
            z.null(),
          ])
          .describe(
            "Day of the month, e.g. 12 — or a preferred weekday ('sat') / list of weekdays (['mon','tue','wed','thu']) when only that is known. null when undecided.",
          ),
      }),
    }),
  }),
  rawAction<typeof changeCurrency>()({
    type: WeddingActionTypes.UPDATE_CURRENCY,
    description:
      "Change the currency the couple's budget and prices are shown in — ONLY when the couple asks in words to change their currency. The couple's CURRENT currency is the `Currency:` line of the live wedding context — they can change it in settings between turns, so that line outranks anything this conversation said earlier. Never send the code already on that line: a currency that is already set has nothing to change. A budget figure in their message, with or without a symbol, is never a reason to call this — the app records the budget in their current currency. IMPORTANT: existing budget amounts are NOT converted — the numbers keep their digits and only the currency label changes, so warn the couple about this before proposing it. Codes Bridebook does not support are refused.",
    // App-wide money display flips and existing budget figures are NOT
    // converted — the settings UI shows an info modal about exactly this.
    access: 'consent',
    // The currency already on the record: settled without a card.
    inForce: (payload, state) => state.weddings.profile.l10n?.currency === payload.value,
    outcome: {
      success: [WeddingActionTypes.UPDATE_CURRENCY_SUCCESS],
      failure: [WeddingActionTypes.UPDATE_CURRENCY_ERROR],
    },
    schema: z.strictObject({
      value: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .describe(
          "ISO 4217 currency code, UPPERCASE, e.g. 'GBP', 'EUR', 'PLN'. Whether Bridebook supports the currency is checked on execution.",
        ),
    }),
  }),
  rawAction<typeof addMultipleGuests>()({
    type: GuestlistActionTypes.ADD_MULTIPLE_GUESTS,
    description:
      "Add one or more guests to the couple's guest list: only people the couple asks to add. Never the couple themselves — the names they introduce themselves with are partner names, and the app saves those. ALWAYS batch every guest into a single action — the names array has no size limit; never emit one action per guest. Use separate actions only when guests belong to different categories (default: the first partner's guests). Names must be unique: the app refuses a name already on the list, saves nothing and says which names clash, so no lookup is needed first.",
    // Easy to undo on the guest list, so no consent gate.
    access: 'open',
    outcome: {
      success: [GuestlistActionTypes.ADD_MULTIPLE_GUESTS_SUCCESS],
      failure: [GuestlistActionTypes.ADD_MULTIPLE_GUESTS_ERROR],
      // The success payload is the raw commit result, so the count comes from
      // what was asked for — the epic writes every requested name or errors.
      describe: (
        _followUp: OutcomeAction,
        requested: ReturnType<typeof addMultipleGuests>['payload'],
      ) => ({
        guestsAdded: requested.names.length,
      }),
    },
    schema: z.strictObject({
      names: z
        .array(z.string().min(1))
        .min(1)
        .describe(
          "Full names of ALL the guests to add, one entry per guest, e.g. ['Anna Nowak', 'Jan Kowalski']. Any number of guests fits in one action.",
        ),
      // Any of the couple's category ids is valid (e.g. one they created in
      // the add-guests popup); the describe() steers the model to the two
      // defaults that exist on every wedding.
      category: z.strictObject({
        id: z
          .string()
          .describe(
            `Guest category id. Default categories: '${DefaultCategories.me}' = the first partner's guests (use this when the message does not say whose guest it is), '${DefaultCategories.partner}' = the second partner's guests.`,
          ),
        value: z
          .string()
          .describe('Category display label. Empty string for the default categories.'),
      }),
    }),
  }),
  rawAction<typeof removeGuestsById>()({
    type: GuestlistActionTypes.REMOVE_GUESTS_BY_ID,
    description:
      "Remove one or more guests from the couple's guest list — use this to undo guests added by mistake. ALWAYS batch every guest into a single action — the guests array has no size limit; never emit one action per guest. Deleting a group's lead guest also detaches their plus-ones. Unknown ids remove nothing.",
    // There is no restore control. Guest details and group links are
    // unrecoverable, and deleting a group's lead guest detaches their plus-ones.
    access: 'consent',
    // When no requested id resolves the epic no-ops, so no follow-up ever
    // comes — waiting then reports { confirmed: false }, which is the honest
    // answer. A partial resolve succeeds with the real count.
    outcome: {
      success: [GuestlistActionTypes.REMOVE_GUESTS_BY_ID_SUCCESS],
      failure: [GuestlistActionTypes.REMOVE_GUESTS_BY_ID_ERROR],
      describe: (followUp: IRemoveGuestsByIdSuccessAction) => ({
        guestsRemoved: followUp.payload?.guestsRemoved ?? 0,
      }),
    },
    schema: z.strictObject({
      guests: z
        .array(
          z.strictObject({
            guestId: z
              .string()
              .min(1)
              .describe(
                "The guest's exact id copied from the findGuests tool — call it first; NEVER invent or guess an id.",
              ),
            guestName: z
              .string()
              .min(1)
              .describe(
                "The guest's name exactly as the guests data lists it — shown to the couple on the approval card. Ask for more specificity if name disambiguation is needed",
              ),
          }),
        )
        .min(1)
        .describe(
          'ALL the guests to remove, one entry per guest. Any number of guests fits in one action.',
        ),
    }),
  }),
  // Consent-gated, and not "just an edit": `notes` and `dietaryOptions` REPLACE rather
  // than merge, and nothing is versioned, so their own free text is destroyed.
  rawAction<typeof updateGuestsById>()({
    type: GuestlistActionTypes.UPDATE_GUESTS_BY_ID,
    description:
      "Edit existing guests' details — any mix of name, RSVP, invitation, dietary needs, contacts, postal address, table, notes and group linking (partner/family/plus-ones). ALWAYS use this to change guests (NEVER remove and re-add them) and ALWAYS batch every guest into this single action — the guests array has no size limit. Address guests by id from findGuests; unknown ids edit nothing. This is also how an imported guest list (pasted CSV/spreadsheet) gets its details: first add all the names, then call findGuests with every filter at all for the fresh ids — its list keeps the order guests were added, so rows map to ids by position even when names repeat — then apply each guest's details here, linking household/partner rows into one group via linkedToGuestId (a very long list may take a few consecutive calls). A row may reference a person who is NOT on the guest list (a partner without their own row): skip that link and tell the couple — never invent an id and never stop mid-action over it. Details with no field here (invitation send dates, transport, accommodation…) have no home in Bridebook: put what matters into notes and tell the couple what was left out.",
    access: 'consent',
    // An unknown-ids-only batch no-ops with no follow-up — waiting reports
    // { confirmed: false }, the honest answer (same as guest removal).
    outcome: {
      success: [GuestlistActionTypes.UPDATE_GUESTS_BY_ID_SUCCESS],
      failure: [GuestlistActionTypes.UPDATE_GUESTS_BY_ID_ERROR],
      describe: (followUp: IUpdateGuestsByIdSuccessAction) => ({
        guestsUpdated: followUp.payload?.guestsUpdated ?? 0,
      }),
    },
    schema: z.strictObject({
      guests: z
        .array(
          z.strictObject({
            guestId: z
              .string()
              .min(1)
              .describe(
                "The guest's exact id copied from the findGuests tool — call it first; NEVER invent or guess an id.",
              ),
            guestName: z
              .string()
              .min(1)
              .describe(
                "The guest's CURRENT name exactly as the guests data lists it — shown to the couple on the approval card. Ask for more specificity if name disambiguation is needed",
              ),
            updates: z
              .strictObject({
                name: z
                  .union([z.string().min(1), z.null()])
                  .optional()
                  .describe("The guest's new full name."),
                category: z
                  .strictObject({
                    id: z
                      .string()
                      .describe(
                        `Guest category id. Default categories: '${DefaultCategories.me}' = the first partner's guests (use this when the message does not say whose guest it is), '${DefaultCategories.partner}' = the second partner's guests.`,
                      ),
                    value: z
                      .string()
                      .describe('Category display label. Empty string for the default categories.'),
                  })
                  .optional()
                  .describe("Move the guest to another category (the couple's guest groups)."),
                rsvp: z
                  .union([z.enum(Rsvp), z.null()])
                  .optional()
                  .describe("RSVP status ('pending' clears it back to no answer)."),
                invitationType: z
                  .union([z.enum(InvitationTypes), z.null()])
                  .optional()
                  .describe("What the guest is invited to ('All Day'/'Evening'/'Waitlist')."),
                dietaryOptions: z
                  .union([z.array(z.enum(GUEST_DIETARY_OPTIONS)), z.null()])
                  .optional()
                  .describe(
                    "Dietary needs AND allergies mapped onto these values (e.g. 'Gluten-free' → glutenFree, a peanut/nut allergy → nutFree, 'Lactose-free' → dairyFree); only what maps to no value goes to other + a note. Replaces the guest's current set.",
                  ),
                email: z
                  .union([z.string().max(200), z.null()])
                  .optional()
                  .describe("The guest's email."),
                phone: z
                  .union([z.string().max(50), z.null()])
                  .optional()
                  .describe("The guest's phone number."),
                address: z
                  .union([
                    z.strictObject({
                      street: z
                        .union([z.array(z.string().max(200)).max(4), z.null()])
                        .optional()
                        .describe('Street line(s).'),
                      city: z
                        .union([z.string().max(100), z.null()])
                        .optional()
                        .describe('City.'),
                      postalCode: z
                        .union([z.string().max(20), z.null()])
                        .optional()
                        .describe('Postal code.'),
                    }),
                    z.null(),
                  ])
                  .optional()
                  .describe("The guest's postal address, split into its parts."),
                table: z
                  .union([z.string().max(100), z.null()])
                  .optional()
                  .describe("Table assignment label, e.g. 'Table 3'."),
                notes: z
                  .union([z.string().max(2000), z.null()])
                  .optional()
                  .describe(
                    'Free-text notes — meal choice, logistics and other details with no field of their own. Replaces the current notes. NEVER build it from a note marked notesTruncated in findGuests: look the guest up by name first for the full note.',
                  ),
                linkedToGuestId: z
                  .union([z.string(), z.null()])
                  .optional()
                  .describe(
                    "Put this guest in another guest's group (their partner/family lead) — that guest's id from findGuests. For imported lists, pick one lead per household/linked-guests set and link the others to it. Empty string detaches the guest from their group. When the person to link has no id in findGuests (not on the guest list), OMIT this field and move on — never invent an id.",
                  ),
              })
              .describe(
                'ONLY the fields to change on this guest — OMIT every field you are not changing (omitted fields stay untouched). Never send a field just to say "keep".',
              ),
          }),
        )
        .min(1)
        .describe('ALL the guests to edit, one entry per guest — any number fits in one action.'),
    }),
  }),
  rawAction<typeof addCustomCategory>()({
    type: GuestlistActionTypes.SAVE_CUSTOM_CATEGORY,
    description:
      "Handles additional (aka custom) groups of guests, in addition of the predefined groups created for each partner of the wedding. The terms guest list, guest group and guest category are interchangeable. Each category only contains an id and a name, and every guest always belongs to a group (through its 'category' field). ALWAYS use this to manage guest groups (NEVER remove and re-add them) and ALWAYS batch every guest group into this single action.",
    // Create or rename, reversible in the category form.
    access: 'open',
    schema: z.strictObject({
      category: z.strictObject({
        id: z
          .string()
          .min(1)
          .describe(
            'A UUID v4 that identifies the group. GENERATE it yourself when creating a new group and get the EXACT id from the guestCategories when updating an existing group.',
          ),
        value: z
          .string()
          .min(1)
          .describe(
            'The guest group name that will be shown to the couple. INFER this from the context or the user input — shown to the couple on the approval card',
          ),
      }),
    }),
  }),
  rawAction<typeof navigateTo>()({
    type: 'NAVIGATE_TO_PATH',
    description:
      'Takes the user to different pages and sections of the app like the invited guests, checklist, home page, etc. It only needs the path to take the user to.',
    access: 'open',
    // The route change is awaited: a push that resolves confirms, and a rejected or empty one
    // reports a failure. So the model hears "done" only after the router has moved.
    outcome: {
      success: ['NAVIGATE_TO_PATH_SUCCESS'],
      failure: ['NAVIGATE_TO_PATH_ERROR'],
      describe: (followUp) => ({
        navigatedTo: (followUp.payload as { path: string }).path,
      }),
    },
    schema: z.strictObject({
      path: z
        .string()
        .describe(
          'The path to take the user to. These are the existing sections: ' +
            toLLM(ProtectedUrls) +
            ' ALWAYS use relative URLs and when necessary, replace the parameters starting with a $ in the instructions with the appropriate value taken from the context.',
        ),
    }),
  }),
  rawAction<typeof setGuestRsvpById>()({
    type: GuestlistActionTypes.SET_GUEST_RSVP_BY_ID,
    description: "Set a guest's RSVP status. Address the guest by id from findGuests.",
    access: 'open',
    // An unknown id no-ops in the epic with no follow-up — waiting reports
    // { confirmed: false }, the honest answer (same as guest removal).
    outcome: {
      success: [GuestlistActionTypes.SET_GUEST_RSVP_STATUS_SUCCESS],
      failure: [GuestlistActionTypes.SET_GUEST_RSVP_STATUS_ERROR],
    },
    schema: z.strictObject({
      guestId: z
        .string()
        .min(1)
        .describe(
          "The guest's exact id copied from the findGuests tool — call it first; NEVER invent or guess an id.",
        ),
      guestName: z
        .string()
        .min(1)
        .describe("The guest's name exactly as the guests data lists it — shown to the couple."),
      attending: z
        .union([z.boolean(), z.null()])
        .describe('true = attending, false = not attending, null = awaiting their reply.'),
    }),
  }),
  rawAction<typeof setSupplierNoteById>()({
    type: ShortlistActionTypes.SET_SUPPLIER_NOTE_BY_ID,
    description:
      "Save a note on one of the couple's shortlisted suppliers (replaces the existing note). Address the supplier by id from the planning snapshot suppliers data.",
    access: 'open',
    schema: z.strictObject({
      supplierId: z
        .string()
        .min(1)
        .describe(
          "The supplier's exact id copied from the planningSnapshot tool's suppliers data — call that tool first; NEVER invent or guess an id.",
        ),
      supplierName: z
        .string()
        .min(1)
        .describe(
          "The supplier's name exactly as the suppliers data lists it — shown to the couple.",
        ),
      note: z.string().min(1).max(2000).describe('The note text; replaces any existing note.'),
    }),
  }),
  rawAction<typeof setTaskDoneById>()({
    type: ChecklistActionTypes.SET_TASK_DONE_BY_ID,
    description:
      'Mark a wedding checklist task, or a whole task group, as done (or not done) by its id from the checklist data (upcoming tasks / planning snapshot). A group id ticks every task in it, as its checkbox in the checklist does. An unknown id fails and ticks nothing.',
    access: 'open',
    outcome: {
      // A subtask settles on the subtask update, a main task or group on the group update.
      success: [
        ChecklistActionTypes.UPDATE_SUBTASKS_SUCCESS,
        ChecklistActionTypes.UPDATE_TASK_SUCCESS,
      ],
      failure: [ChecklistActionTypes.TASK_WRITE_ERROR],
    },
    schema: z.strictObject({
      taskId: z
        .string()
        .min(1)
        .describe(
          "The task's exact id, or its groupId to tick the whole group, copied from the findChecklistTasks tool — call it first; NEVER invent or guess an id.",
        ),
      done: z.boolean().describe('true = mark as done, false = mark as not done.'),
    }),
  }),
  rawAction<typeof addTask>()({
    type: ChecklistActionTypes.ADD_TASK,
    description:
      "Add a custom task to the couple's checklist: a main task, or a subtask inside a Bridebook group (never under a custom task). Needs a started checklist.",
    access: 'open',
    outcome: {
      ...TASK_WRITE_OUTCOME,
      describe: (followUp: ITaskWriteSuccessAction) => ({ taskId: followUp.payload?.taskId }),
    },
    schema: z.strictObject({
      name: TASK_NAME.describe("The task name, short, in the couple's words."),
      period: CHECKLIST_PERIOD.describe(
        "When it is due. A calendar month the couple names as 'YYYY-MM', from now until the wedding: it lands in that month of their checklist, the wedding month itself in its first slot (usually the week before), and a month with no slot of its own (two months before) in the slot before it. '-P{n}M' only when they say n months before the wedding; '-P1W' = the week before, 'P' = the wedding day, '+P' = after it. A subtask takes its group's period.",
      ),
      groupId: z
        .union([z.string().min(1), z.null()])
        .describe(
          'null for a main task, or for a subtask the groupId of a Bridebook task (custom: false) from findChecklistTasks. Custom tasks take no subtasks.',
        ),
    }),
  }),
  rawAction<typeof renameTaskById>()({
    type: ChecklistActionTypes.RENAME_TASK_BY_ID,
    description:
      'Rename a custom checklist task (custom: true) by its id. Bridebook tasks cannot be renamed.',
    access: 'open',
    outcome: TASK_WRITE_OUTCOME,
    schema: z.strictObject({
      taskId: CUSTOM_TASK_ID,
      name: TASK_NAME.describe('The new task name.'),
    }),
  }),
  rawAction<typeof changeTaskPeriod>()({
    type: ChecklistActionTypes.TASKS_PERIOD_CHANGE,
    description:
      'Move a checklist task group (a main task with its subtasks) to another period, as the period dropdown on a task does.',
    access: 'open',
    outcome: {
      success: [ChecklistActionTypes.TASKS_PERIOD_CHANGE_SUCCESS],
      failure: [ChecklistActionTypes.TASK_WRITE_ERROR],
    },
    schema: z.strictObject({
      groupId: z
        .string()
        .min(1)
        .describe('The groupId from findChecklistTasks — NEVER invent or guess an id.'),
      period: CHECKLIST_PERIOD.describe(
        'The new period, in the same format as when adding a task.',
      ),
    }),
  }),
  rawAction<typeof setTaskGroupById>()({
    type: ChecklistActionTypes.SET_TASK_GROUP_BY_ID,
    description:
      "Move a custom task (custom: true) into a Bridebook group as a subtask, taking that group's period, or out of its group as a main task. Custom tasks take no subtasks, so a custom task never goes under another custom task.",
    access: 'open',
    outcome: TASK_WRITE_OUTCOME,
    schema: z.strictObject({
      taskId: CUSTOM_TASK_ID,
      groupId: z
        .union([z.string().min(1), z.null()])
        .describe(
          'The groupId of a Bridebook task (custom: false) from findChecklistTasks, or null to make it a main task.',
        ),
    }),
  }),
  rawAction<typeof deleteCustomTaskById>()({
    type: ChecklistActionTypes.DELETE_CUSTOM_TASK_BY_ID,
    description:
      'Delete a custom checklist task (custom: true) by its id. A main task goes with all its subtasks, a subtask on its own: the app tells them apart. Bridebook tasks cannot be deleted.',
    // Nothing restores a deleted task.
    access: 'consent',
    outcome: TASK_WRITE_OUTCOME,
    schema: z.strictObject({
      taskId: CUSTOM_TASK_ID,
    }),
  }),
] as const;
